import { Checkbox, Select, spacing } from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { css } from '@emotion/react'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { CollectionsFormInputWrapper } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormInputWrapper'
import { useFetchWarehouses } from './utils/useFetchWarehouses.hooks'

interface CollectionsDetailsWarehouseProps {
  register: FormContextValues<CollectionsFormData>['register']
  getValues: FormContextValues<CollectionsFormData>['getValues']
  setValue: FormContextValues<CollectionsFormData>['setValue']
}

interface WarehouseOption {
  label: string
  value: string
}

const wrapper = css({
  display: 'flex',
  marginBottom: spacing.X16,
})

const selectWrapper = css({
  width: 315,
})

const checkboxWrapper = css({
  display: 'flex',
  alignItems: 'center',
  marginLeft: spacing.X16,
})

export const CollectionsDetailsWarehouse: FunctionComponent<CollectionsDetailsWarehouseProps> = ({
  register,
  getValues,
  setValue,
}) => {
  const messages = useDomainMessages({
    label: 'collectionsDomain.collectionsForm.section.details.applicableWarehouses.label',
    applyToAll: 'collectionsDomain.collectionsForm.section.details.applicableWarehouses.applyToAll',
  })

  const [applyToAll, setApplyToAll] = useState<boolean>(true)
  const [selectedWarehouses, setSelectedWarehouses] = useState<WarehouseOption[]>([])

  const { warehouses, loading } = useFetchWarehouses()
  const warehouseOptions: WarehouseOption[] = useMemo(() => {
    if (warehouses) {
      return [
        ...warehouses
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ id, name }) => ({
            label: `${name} (id: ${id})`,
            value: id,
          })),
      ]
    }
  }, [warehouses])

  const handleOnChange = useCallback(
    (selectedWarehouses: WarehouseOption[]) => {
      setSelectedWarehouses(selectedWarehouses)
      setValue(
        'details.retailerIds',
        selectedWarehouses.map(({ value }) => value)
      )
    },
    [setValue]
  )

  const handleApplyAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setApplyToAll(e.target.checked)
      if (e.target.checked) {
        setSelectedWarehouses([])
        setValue('details.retailerIds', [])
      }
    },
    [setValue]
  )

  useEffect(() => {
    register({ name: 'details.retailerIds' })
  }, [register])

  useEffect(() => {
    // initialize selectedWarehouses when editing a collection
    const retailerIds = getValues('details.retailerIds') as string[]
    if (retailerIds && warehouseOptions) {
      const selectedWarehouses = warehouseOptions.filter(({ value }) => retailerIds.includes(value))

      if (selectedWarehouses) {
        setSelectedWarehouses(selectedWarehouses)
        setApplyToAll(false)
      }
    }
  }, [getValues, warehouseOptions, setSelectedWarehouses])

  return (
    <CollectionsFormInputWrapper label={messages.label}>
      <div css={wrapper}>
        <div css={selectWrapper}>
          <Select
            name="details.retailerIds"
            options={warehouseOptions}
            defaultValue={selectedWarehouses}
            isDisabled={applyToAll}
            isMulti={true}
            isLoading={loading}
            placeholder={messages.label}
            onChange={handleOnChange}
            value={selectedWarehouses}
          />
        </div>

        <div css={checkboxWrapper}>
          <Checkbox id="applyToAll" checked={applyToAll} onChange={handleApplyAllChange} />
          <label className="checkbox-label" htmlFor="applyToAll">
            {messages.applyToAll}
          </label>
        </div>
      </div>
    </CollectionsFormInputWrapper>
  )
}
